import {
  startOfDay,
  endOfDay,
  startOfMonth,
  addDays,
  parseISO,
  setMonth,
  setYear,
} from "date-fns";
import moment from "moment";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { utcToZonedTime } from "date-fns-tz";
import { minDate } from "../../utils/datepicker-dates";
import { PostmatchPickerCustomProps } from "./postmatch-picker-custom.types";
import {
  ActivePicker,
  PickerDateRange,
} from "../../../../types/postmatch.types";
import {
  getQueryDatesInISO,
  isPickerFromQueryValid,
} from "../../../../utils/query/query";
import { getMonthFromISO, getYearFromISO } from "../../../../utils/dates/dates";
import { ActiveMonthYear } from "../../postmatch-picker.types";

function getInitialPickerDate(
  query: URLSearchParams,
  isQueryLoaded: boolean,
  activeMonthYear: ActiveMonthYear,
) {
  const { startDateFromQueryISO, endDateFromQueryISO } =
    getQueryDatesInISO(query);
  if (
    getMonthFromISO(startDateFromQueryISO) !== activeMonthYear.month ||
    getYearFromISO(startDateFromQueryISO) !== activeMonthYear.year
  ) {
    const initialStartDate = startOfMonth(
      setYear(
        setMonth(new Date(), activeMonthYear.month),
        activeMonthYear.year,
      ),
    );
    const initialEndDate = addDays(initialStartDate, 9);
    return { initialStartDate, initialEndDate };
  }
  if (
    isQueryLoaded &&
    query.get("pickerMode") === ActivePicker.Custom &&
    isPickerFromQueryValid(
      ActivePicker.Custom,
      startDateFromQueryISO,
      endDateFromQueryISO,
    )
  ) {
    return {
      initialStartDate: utcToZonedTime(parseISO(startDateFromQueryISO), "UTC"),
      initialEndDate: utcToZonedTime(parseISO(endDateFromQueryISO), "UTC"),
    };
  }
  const initialStartDate = startOfMonth(
    utcToZonedTime(parseISO(startDateFromQueryISO), "UTC"),
  );
  const initialEndDate = addDays(initialStartDate, 9);
  return { initialStartDate, initialEndDate };
}

export function PostmatchPickerCustom({
  setPopoverDate,
  isQueryLoaded,
  query,
  activeMonthYear,
  setActiveMonthYear,
  setVisibleCustomDate,
  setDateBoxHighlight,
}: PostmatchPickerCustomProps) {
  const { initialStartDate, initialEndDate } = getInitialPickerDate(
    query,
    isQueryLoaded,
    activeMonthYear,
  );
  const [overridableMinDate, setOverridableMinDate] = useState(minDate);

  const [pickerDateRange, setPickerDateRange] = useState<PickerDateRange>({
    start: initialStartDate,
    end: initialEndDate,
  });

  useEffect(() => {
    const startDateUTC = moment(initialStartDate).utc(true).toISOString();
    const endDateUTC = moment(initialEndDate).utc(true).toISOString();
    setPopoverDate({ start: startDateUTC, end: endDateUTC });
    setVisibleCustomDate({ start: startDateUTC, end: endDateUTC });
    setPickerDateRange({ start: initialStartDate, end: initialEndDate });
  }, [isQueryLoaded]);

  const onChange = (dates: [Date, Date | null]) => {
    const [startDate, endDate] = dates;
    setPickerDateRange({
      start: startDate,
      end: endDate,
    });
    const startDateUTC = moment(startOfDay(startDate)).utc(true).toISOString();
    if (startDate && endDate === null) {
      setOverridableMinDate(startDate);
      setVisibleCustomDate({ start: startDateUTC, end: "" });
      setDateBoxHighlight({ start: false, end: true });
    }
    if (startDate && endDate) {
      setOverridableMinDate(minDate);
      const endDateUTC = moment(endOfDay(endDate)).utc(true).toISOString();
      setPopoverDate({ start: startDateUTC, end: endDateUTC });
      setVisibleCustomDate({ start: startDateUTC, end: endDateUTC });
      setDateBoxHighlight({ start: true, end: false });
    }
  };

  const onMonthChange = (date: Date) => {
    setActiveMonthYear({ month: date.getMonth(), year: date.getFullYear() });
  };

  return (
    <DatePicker
      selected={pickerDateRange.start}
      onChange={onChange}
      onMonthChange={onMonthChange}
      startDate={pickerDateRange.start}
      endDate={pickerDateRange.end}
      selectsRange
      inline
      minDate={overridableMinDate}
    />
  );
}
